import { Flex, Text, Badge, Icon, HoverCard } from '@scale/scaleui-radix';
import { AuditRubricStatus, AuditRubric, AuditRubricCriterion } from '@scale/shared-types';
import { faNotes, faHourglassClock } from '@fortawesome/pro-regular-svg-icons';
import { v4 as uuidv4 } from 'uuid';
import { formatDate } from '@scale/frontend-lib/dateFnUtils';

export const NEW_DRAFT_RUBRIC_ID = 'NEW-DRAFT';

export type DefaultAuditRubric = Omit<AuditRubric, 'createdAt' | 'updatedAt'>;

export const getDefaultAuditRubric = (
  projectId: string,
  userId: string,
  name: string,
  taskTemplateVersion: number,
  baseRubric?: AuditRubric,
): DefaultAuditRubric => ({
  id: NEW_DRAFT_RUBRIC_ID,
  name,
  criteria: baseRubric ? baseRubric.criteria : [],
  status: AuditRubricStatus.Draft,
  project: projectId,
  createdBy: userId,
  taskTemplateVersion: baseRubric ? baseRubric.taskTemplateVersion : taskTemplateVersion,
});

export enum DialogType {
  NEW = 'new',
  RENAME = 'rename',
}

export const getNewDefaultCriterion = (): AuditRubricCriterion => ({
  id: uuidv4(),
  title: '',
  questionText: '',
  answerOptions: [{ text: '', score: 3 }],
  useAllSteps: false,
  steps: [],
  useAllErrorCategories: false,
  errorCategories: [],
  requiresJustification: true,
});

export enum CriterionIssueType {
  Name = 'Name',
  AnswerOptions = 'AnswerOptions',
  Steps = 'Steps',
  ErrorCategories = 'ErrorCategories',
  Justification = 'Justification',
}

export const getCriterionIssues = (
  criterion: AuditRubricCriterion,
): Partial<Record<CriterionIssueType, string>> => {
  const issues: Partial<Record<CriterionIssueType, string>> = {};
  if (criterion.title.length === 0) {
    issues[CriterionIssueType.Name] = 'Criterion name cannot be empty';
  }
  if (!criterion.useAllSteps && criterion.steps.length === 0) {
    issues[CriterionIssueType.Steps] = 'Select at least one applicable step';
  }
  if (criterion.answerOptions.length === 0) {
    issues[CriterionIssueType.AnswerOptions] = 'Define at least one rating option';
  }
  if (criterion.answerOptions.some(option => option.text.length === 0)) {
    issues[CriterionIssueType.AnswerOptions] = 'Please enter a text for each answer option';
  }
  if (criterion.answerOptions.some(option => option.score < 1 || option.score > 5)) {
    issues[CriterionIssueType.AnswerOptions] =
      'Scores can only be between 1 and 5 for each answer option';
  }
  return issues;
};

export interface SelectRubricOption {
  id: string;
  name: string;
  description?: string;
}

const statusToHoverCard = (rubric: AuditRubric) => {
  if (rubric.status === AuditRubricStatus.Approved) {
    return (
      <Flex direction="column" gap="1">
        <Text size="1">Created at {formatDate(rubric.createdAt)}</Text>
        <Text size="1">Version: {rubric.version ?? 'Error'}</Text>
      </Flex>
    );
  }
};

const statusToIcon = (status: AuditRubricStatus) => {
  switch (status) {
    case AuditRubricStatus.Draft:
      return faNotes;
    case AuditRubricStatus.Pending:
      return faHourglassClock;
  }
};

const statusToColor = (
  status: AuditRubricStatus,
  isHistorical: boolean,
): 'neutral' | 'success' | 'error' | 'warning' => {
  switch (status) {
    case AuditRubricStatus.Draft:
      return 'neutral';
    case AuditRubricStatus.Pending:
      return 'warning';
    case AuditRubricStatus.Approved:
      return isHistorical ? 'neutral' : 'success';
    case AuditRubricStatus.Archived:
      return 'error';
  }
};

export const statusToText = (status: AuditRubricStatus, isHistorical?: boolean) => {
  switch (status) {
    case AuditRubricStatus.Draft:
      return 'Draft';
    case AuditRubricStatus.Pending:
      return 'Pending QC Review';
    case AuditRubricStatus.Approved:
      return isHistorical ? 'Historical' : 'Live';
    case AuditRubricStatus.Archived:
      return 'Archived';
  }
};

export const renderRubricStatusBadge = (rubric: AuditRubric, isHistorical: boolean) => {
  if (statusToText(rubric.status, isHistorical)) {
    const badge = (
      <Badge color={statusToColor(rubric.status, isHistorical)}>
        {statusToIcon(rubric.status) && (
          <Icon
            color={statusToColor(rubric.status, isHistorical)}
            icon={statusToIcon(rubric.status)!}
            label={statusToText(rubric.status, isHistorical)}
          />
        )}
        {statusToText(rubric.status, isHistorical)}
      </Badge>
    );
    const HoverCardContent = statusToHoverCard(rubric);
    return HoverCardContent ? (
      <HoverCard.Root>
        <HoverCard.Trigger>{badge}</HoverCard.Trigger>
        <HoverCard.Content size="1" maxWidth="300px">
          {HoverCardContent}
        </HoverCard.Content>
      </HoverCard.Root>
    ) : (
      badge
    );
  }
  return null;
};
